
import { mapGetters, useStore } from 'vuex';
import slider from '@/components/inputs/slider.vue';
import incrediInputNumWithMenu from '@/components/inputs/incrediInputNumWithMenu.vue';
import TooltipBtn from '@/components/common/TooltipBtn.vue';
import IncrediCheckBox from '@/components/inputs/incrediCheckBox.vue';
import { PropType, defineComponent, inject } from 'vue';
import objectPath from 'object-path';
import { isBuildAvoidanceLicensed } from '@/globals/functions/licenseValidationRules';
import { IKSettingsDirty } from '@/types/injectionKeys';
import { QUERY_RESULT_PATH, SettingsPageWindowsQuery } from '@/graphql/codegen/generated/graphql';
import elementsIds from '@/const/elementIds.json';
import { useUserRolePermissions } from '@/services/UserRolePermissions/UserRolePermissions';
import { useMatomo } from '@/services/matomo/matomo.service';
import SettingsDefaultAgentLicense from '@/views/settings/components/settingsDefaultAgentLicense/settings-default-agent-license-value.vue';
import { SettingsResultPaths } from '@/views/settings/settings.types';
import SettingsItemBox from './components/settings-item-box.vue';
import util from './util';

export default defineComponent({
  name: 'SettingsAgents',
  components: {
    SettingsDefaultAgentLicense,
    IncrediCheckBox,
    slider,
    incrediInputNumWithMenu,
    SettingsItemBox,
    TooltipBtn,
  },
  props: {
    settingsObject: {
      type: Object as PropType<NonNullable<SettingsPageWindowsQuery['ibManagerEntities']>>,
      required: true,
    },
    handleInputWithMenuSelection: {
      required: true,
      type: Function as PropType<() => void>,
    },
  },
  emits: ['input'],
  setup() {
    const userRolePermissions = useUserRolePermissions();
    const store = useStore();
    const settingsDirty = inject(IKSettingsDirty);
    const matomo = useMatomo();
    return {
      userRolePermissions,
      store,
      objectPath,
      QUERY_RESULT_PATH,
      settingsDirty,
      elementsIds,
      isBuildAvoidanceLicensed,
      matomo,
    };
  },

  data() {
    return {
      ignoreCpuProcess: false,
      processToAdd: '',
    };
  },
  computed: {
    util() {
      return util;
    },
    license() {
      return this.settingsObject.license;
    },
    ignoredProcessesList() {
      return this.settingsObject.settings?.windows?.agent?.ignoreProcesses;
    },
    ...mapGetters({
      inputsWithMenuSelectedOption: ['settings/getInputsWithMenuSelectedOption'] as any,
    }),
  },
  watch: {
    'settingsObject?.settings?.windows?.agent?.ignoreProcesses': {
      handler() {
        if (!this.settingsObject?.settings?.windows?.agent?.ignoreProcesses) {
          this.ignoreCpuProcess = false;
        }
      },
    },
  },
  mounted() {},
  created() {
    this.store.commit('settings/setInputWithMenuSelectedOption', {
      'time-period-space-component': 'DAYS',
    });
  },
  beforeMount() {
    this.ignoreCpuProcess = !!this.ignoredProcessesList;
  },
  methods: {
    handleInputIntervalChange(newVal: any, selectedOption: any, path: SettingsResultPaths, min: number, max: number) {
      const val: any = util.convertTimeToDays(newVal, selectedOption);
      if (val < min) {
        this.settingsDirty?.setInputsDirty(path, min);
      } else if (val > max) {
        this.settingsDirty?.setInputsDirty(path, max);
      } else {
        this.settingsDirty?.setInputsDirty(path, Number(val));
      }
    },
    handleIgnoreProcesses() {
      this.ignoreCpuProcess = !this.ignoreCpuProcess;
      this.settingsDirty?.setInputsDirty(QUERY_RESULT_PATH.settings.windows.agent.ignoreProcesses, '');
    },
    addProcess() {
      if (this.processToAdd) {
        const existInList = this.ignoredProcessesList ? this.ignoredProcessesList.includes(this.processToAdd) : false;
        if (existInList) {
          this.processToAdd = '';
          return;
        }
        let ignoredList = this.ignoredProcessesList || '';
        ignoredList += ignoredList === '' ? this.processToAdd : `,${this.processToAdd}`;
        this.settingsDirty?.setInputsDirty(QUERY_RESULT_PATH.settings.windows.agent.ignoreProcesses, ignoredList || '');

        this.processToAdd = '';
      }
    },
    removeProcess(process: any) {
      if (process) {
        const existInList = this.ignoredProcessesList ? this.ignoredProcessesList.includes(process) : false;
        let ignoredList = this.ignoredProcessesList ? this.ignoredProcessesList.split(',') : '';
        if (existInList) {
          if (Array.isArray(ignoredList) && ignoredList.indexOf(process) > -1) {
            ignoredList.splice(ignoredList.indexOf(process), 1);
          }
          ignoredList = Array.isArray(ignoredList) && ignoredList ? ignoredList.join(',') : '';
          this.settingsDirty?.setInputsDirty(QUERY_RESULT_PATH.settings.windows.agent.ignoreProcesses, ignoredList || '');
        }
      }
    },
  },
});
